import { useEffect, useId, useRef, useState } from 'react'

/**
 * The controls the Catalog needed and did not have, and the Stock tab reuses.
 *
 * They live together because they answer one question — *how does somebody who
 * has never seen this app read a screen about a robot* — and the answer is the
 * same few moves each time: explain the jargon where it is used, take one thing
 * at a time, do not throw away the page behind you, and let the number somebody
 * came for be the thing the eye lands on.
 */

// --- a hint ------------------------------------------------------------------

/**
 * An ⓘ that explains a word.
 *
 * A `title` attribute would be a third of the code and it is the wrong control:
 * it never appears on a touch screen, and half the people using this are on a
 * phone at a bench. So it is a real button with a real popover — tap to open,
 * tap anywhere or press Escape to close.
 *
 * The words matter more than the mechanism. "Serialized — a swap unit with its
 * own life" is a sentence that means something once you already know what it
 * means, which is the definition of jargon.
 */
export function Hint({ children }: { children: React.ReactNode }): JSX.Element {
  const [open, setOpen] = useState(false)
  const id = useId()
  const wrap = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) return
    const close = (e: Event): void => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false)
    }
    const escape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  return (
    <span className="hint" ref={wrap}>
      <button
        type="button"
        className="hint__button"
        aria-expanded={open}
        aria-controls={id}
        aria-label="What does this mean?"
        onClick={() => setOpen((o) => !o)}
      >
        i
      </button>
      {open ? (
        <span className="hint__body" id={id} role="tooltip">
          {children}
        </span>
      ) : null}
    </span>
  )
}

/** A label with its explanation attached, so the two cannot drift apart. */
export function FieldLabel({
  label,
  hint,
}: {
  label: string
  hint?: React.ReactNode
}): JSX.Element {
  return (
    <span className="field-label">
      {label}
      {hint ? <Hint>{hint}</Hint> : null}
    </span>
  )
}

// --- a modal -----------------------------------------------------------------

/**
 * A dialog, on the page rather than instead of it.
 *
 * Native `<dialog>`: it traps focus, closes on Escape, and paints its own
 * backdrop — all things a hand-rolled overlay gets subtly wrong, usually for
 * whoever is using a keyboard or a screen reader. Filling in a form should not
 * cost you the list you were looking at.
 */
export function Modal({
  title,
  subtitle,
  onClose,
  children,
  footer,
  className = '',
}: {
  title: string
  subtitle?: React.ReactNode
  onClose: () => void
  children: React.ReactNode
  footer?: React.ReactNode
  /** A variant — `modal--sheet` is the item editor's wide dialog, full screen on a phone. */
  className?: string
}): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (!dialog.open) {
      dialog.showModal()
      /*
       * Where the focus lands when a dialog opens.
       *
       * `showModal()` focuses the first focusable element unless something
       * claims `autofocus`, and the first focusable element here is the ✕ —
       * so every dialog opened with a bright ring drawn around its close
       * button, which is both ugly and the wrong suggestion about what to do
       * next.
       *
       * The panel takes it instead. Focus is still inside the dialog, so
       * Escape and the focus trap work and a screen reader reads the title;
       * it simply is not sitting on "give up". A dialog that wants a field
       * focused marks it `data-autofocus` and this hands it over — notably
       * *not* the Receive loop, which must not focus an input, because on a
       * phone that opens the keyboard over the viewfinder.
       *
       * `data-autofocus` rather than React's `autoFocus`: React focuses on
       * mount, and `showModal()` runs its own focusing steps afterwards and
       * takes it straight back. Doing it here is the only point that is
       * reliably *after* the dialog has had its say.
       */
      const wanted = dialog.querySelector<HTMLElement>('[data-autofocus]')
      ;(wanted ?? dialog.querySelector<HTMLElement>('.modal__panel'))?.focus()
    }
    // Escape fires `cancel`; the close button and the backdrop call `onClose`
    // directly. Both routes end in one place so a half-closed dialog is not a
    // state that exists.
    const cancel = (e: Event): void => {
      e.preventDefault()
      onClose()
    }
    dialog.addEventListener('cancel', cancel)
    return () => dialog.removeEventListener('cancel', cancel)
  }, [onClose])

  return (
    <dialog
      className={`modal${className ? ` ${className}` : ''}`}
      ref={ref}
      onClick={(e) => {
        // A click on the dialog element itself is a click on the backdrop: the
        // panel inside stops its own.
        if (e.target === ref.current) onClose()
      }}
    >
      {/* `tabIndex={-1}` so the panel can hold focus on open without entering
          the tab order — see the effect above. */}
      <div className="modal__panel" tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        <header className="modal__head">
          <div className="stack" style={{ gap: 2 }}>
            <h2 className="modal__title">{title}</h2>
            {subtitle ? <span className="muted">{subtitle}</span> : null}
          </div>
          <button type="button" className="modal__close" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </header>
        <div className="modal__body">{children}</div>
        {footer ? <footer className="modal__foot">{footer}</footer> : null}
      </div>
    </dialog>
  )
}

// --- chips -------------------------------------------------------------------

/**
 * A list of short names, added **one at a time**.
 *
 * A comma-separated text box looks like less work and is not: it asks somebody
 * to remember the separator, hides typos inside a long string, gives no way to
 * remove the third one without re-reading the lot, and quietly creates
 * `torque_time ` with a trailing space — which is a *different counter* from
 * `torque_time` for the rest of this app's life, because these names are keys
 * that a robot's agent reports against.
 */
export function ChipInput({
  values,
  onChange,
  placeholder,
  normalise = (v) => v.trim(),
}: {
  values: readonly string[]
  onChange: (next: readonly string[]) => void
  placeholder?: string
  normalise?: (raw: string) => string
}): JSX.Element {
  const [draft, setDraft] = useState('')

  const add = (): void => {
    const value = normalise(draft)
    if (value === '' || values.includes(value)) {
      setDraft('')
      return
    }
    onChange([...values, value])
    setDraft('')
  }

  return (
    <div className="chips">
      {values.length > 0 ? (
        <div className="chips__list">
          {values.map((value) => (
            <span key={value} className="chip">
              <code>{value}</code>
              <button
                type="button"
                aria-label={`Remove ${value}`}
                onClick={() => onChange(values.filter((v) => v !== value))}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <div className="row" style={{ gap: 8 }}>
        <input
          value={draft}
          placeholder={placeholder}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter and comma both add, because somebody who has used a tag
            // field before will reach for one of them and neither should submit
            // the form by accident.
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault()
              add()
            }
            if (e.key === 'Backspace' && draft === '' && values.length > 0) {
              onChange(values.slice(0, -1))
            }
          }}
          // Losing focus with something typed should keep it, not bin it.
          onBlur={add}
        />
        <button type="button" onClick={add} disabled={normalise(draft) === ''}>
          Add
        </button>
      </div>
    </div>
  )
}

/**
 * Counter and measurement names are keys, not prose.
 *
 * The agent reports against them and a trigger matches on them, so `Torque Time`
 * and `torque_time` being two different things is a bug waiting for December.
 * The server normalises too; this makes it visible while somebody is typing.
 */
export function asKey(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

// --- a figure ----------------------------------------------------------------

/**
 * A number and what it counts.
 *
 * Somebody opens Stock to find out whether there are any left, so the number is
 * the largest thing on the card — not a badge in a row of badges, where the eye
 * has to read three of them before it finds the one it came for. Tabular
 * figures so a column of them lines up, and `--warning-text` at zero: an empty
 * shelf is the answer that changes what happens next.
 */
export function Stat({
  n,
  label,
  lead = false,
  zero = false,
}: {
  n: number
  label: string
  lead?: boolean
  zero?: boolean
}): JSX.Element {
  return (
    <span className="stat">
      <span className={`stat__n ${lead ? 'stat__n--lead' : ''} ${zero ? 'stat__n--zero' : ''}`}>
        {n}
      </span>
      <span className="stat__label">{label}</span>
    </span>
  )
}
